import { useEffect, useState } from 'react';

import { Copy } from 'lucide-react';
import { toast } from 'sonner';

import { copyToClipboard } from '@nesso/ui/lib/clipboard';
import { Textarea } from '@nesso/ui/primitives/textarea';

import { SOAP_FIELD_MAX_LENGTH } from '@/entities/session-note';
import { isNotEmpty } from '@/shared/lib/object-utils';
import { PopoverTooltipButton } from '@/shared/ui/common/popover-tooltip-button';

interface Props {
    title: string;
    content: string;
    onContentChange: (content: string) => void;
}

async function copySectionContent(content: string, title: string) {
    const copied = await copyToClipboard(content);
    toast(copied ? `${title} copied` : `Could not copy ${title} — please try again`);
}

export function SessionNoteSection({ title, content, onContentChange }: Props) {
    const [draft, setDraft] = useState(content);

    useEffect(() => {
        setDraft(content);
    }, [content]);

    const handleChange = (value: string) => {
        const next = value.slice(0, SOAP_FIELD_MAX_LENGTH);
        setDraft(next);
        onContentChange(next);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <p className="">{title}</p>
                <PopoverTooltipButton
                    tooltipContent={<p className="text-sm font-light">Copy {title}</p>}
                    tooltipContentProps={{ showArrow: false, className: 'px-2 py-1 text-sm' }}
                    buttonProps={{
                        variant: 'ghost',
                        size: 'icon',
                        className: 'h-6 w-6',
                        'aria-label': `Copy ${title}`,
                        disabled: !isNotEmpty(draft),
                        onClick: () => copySectionContent(draft, title),
                    }}
                >
                    <Copy className="w-4 h-4" />
                </PopoverTooltipButton>
            </div>
            <Textarea
                value={draft}
                onChange={(e) => handleChange(e.target.value)}
                className="min-h-[6.25rem] sm:max-h-[62.5rem] field-sizing-content overflow-y-auto resize-none leading-relaxed bg-background dark:bg-input/30"
                placeholder={`Enter ${title.toLowerCase()} information...`}
                maxLength={SOAP_FIELD_MAX_LENGTH}
            />
        </div>
    );
}
